import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  Menu, X, Sparkles, Instagram, Linkedin, Facebook, Twitter, Mail, MapPin, Phone,
} from "lucide-react";

// Routes that use their own sidebar shell — hide the marketing navbar + footer
const DASHBOARD_ROUTES = ["/admin", "/dashboard", "/referrals", "/pending", "/directory", "/members", "/members-map", "/documents", "/p", "/learning", "/chapters", "/pitch-ask", "/feedback", "/create", "/contracts", "/admin/gallery", "/admin/chapters"];

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/membership", label: "Membership" },
  { to: "/events", label: "Events" },
  { to: "/gallery", label: "Gallery" },
  { to: "/blog", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    fn();
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-ivory/80 border-b border-aubergine/5 shadow-luxe" : "bg-ivory"
      }`}
    >
      <div className="container-luxe flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img
            src="/wowshe-logo.png"
            alt="WOWSHE"
            className="h-12 md:h-16 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm tracking-wide transition-colors ${
                  isActive ? "text-plum" : "text-aubergine/70 hover:text-aubergine"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <Link to="/dashboard" data-testid="nav-dashboard" className="btn-ghost !py-2 !px-5 text-xs">
                Dashboard
              </Link>
              <button data-testid="nav-logout" onClick={logout} className="btn-primary !py-2 !px-5 text-xs">
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm text-aubergine/80 hover:text-aubergine">
                Sign in
              </Link>
              <Link to="/membership" data-testid="nav-join" className="btn-primary !py-2.5 !px-6 text-xs">
                Join WOWSHE
              </Link>
            </>
          )}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-aubergine"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-aubergine/5 bg-ivory">
          <div className="container-luxe py-6 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`m-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-3 text-base ${isActive ? "text-plum" : "text-aubergine/80"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="mt-4 flex flex-col gap-3">
              {user ? (
                <>
                  <Link to="/dashboard" className="btn-ghost">Dashboard</Link>
                  <button onClick={logout} className="btn-primary">Sign out</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="btn-ghost">Sign in</Link>
                  <Link to="/membership" className="btn-primary">Join WOWSHE</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="bg-aubergine text-ivory relative overflow-hidden">
      <div className="absolute inset-0 grain opacity-30" />
      <div className="container-luxe py-20 relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Link to="/" className="inline-block">
              <img src="/wowshe-logo.png" alt="WOWSHE" className="h-28 w-auto object-contain" />
            </Link>
            <p className="mt-6 text-ivory/70 leading-relaxed max-w-md font-light">
              Launching in <span className="text-champagne">Hyderabad</span> · 2026.
              An invitation-grade women-only community focused on empowerment workshops,
              health & wellness, and CSR for women & girls. Founding cohort applications open.
            </p>
            <div className="mt-8 flex gap-4">
              {[Instagram, Linkedin, Facebook, Twitter].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  data-testid={`footer-social-${i}`}
                  className="w-10 h-10 rounded-full border border-ivory/15 flex items-center justify-center hover:bg-champagne hover:text-aubergine hover:border-champagne transition-all"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="overline !text-champagne mb-5">Community</h4>
            <ul className="space-y-3 text-ivory/75 text-sm">
              <li><Link className="link-underline" to="/about">About</Link></li>
              <li><Link className="link-underline" to="/membership">Membership</Link></li>
              <li><Link className="link-underline" to="/directory">Member Directory</Link></li>
              <li><Link className="link-underline" to="/stories">Success Stories</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="overline !text-champagne mb-5">Programs</h4>
            <ul className="space-y-3 text-ivory/75 text-sm">
              <li><Link className="link-underline" to="/events">Events</Link></li>
              <li><Link className="link-underline" to="/gallery">Gallery</Link></li>
              <li><Link className="link-underline" to="/blog">Journal</Link></li>
              <li><Link className="link-underline" to="/contact">Contact</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="overline !text-champagne mb-5">Reach Us</h4>
            <ul className="space-y-3 text-ivory/75 text-sm">
              <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5" /><span>hello@wowshe.in</span></li>
              <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5" /><a href="tel:+917095414575" className="hover:text-ivory">+91 70954 14575</a></li>
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5" /><span>Hyderabad, Telangana · India</span></li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-ivory/10 flex flex-col md:flex-row justify-between gap-4 text-xs text-ivory/50">
          <p>© {new Date().getFullYear()} WOWSHE — Women of Wisdom, Success, Happiness & Empowerment.</p>
          <p>Crafted in India · For women, by women.</p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  const loc = useLocation();
  const isDashboard = DASHBOARD_ROUTES.some((p) => loc.pathname === p || loc.pathname.startsWith(p + "/"));
  if (isDashboard) {
    // Dashboard pages render their own DashboardShell with sidebar — skip marketing chrome
    return <div className="min-h-screen bg-ivory">{children}</div>;
  }
  return (
    <div className="min-h-screen flex flex-col bg-ivory">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
